import type { MouseEvent as ReactMouseEvent } from "react";
import type { TFunction } from "i18next";
import type { Field } from "../util/graphFieldsExtractor";
import type { Control, ControllerRenderProps, UseFormRegister } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { Checkbox, ColorField, Dropdown, Input } from "../../../common/components/Input";
import type { FieldSelectOption } from "../util/FieldDescriptor";
import type { IMedia } from "../../../model/unityObject";
import { emptyOrganization } from "../../profile/state/profileSlice";
import { MediaSelector } from "./MediaSelector";
import { useTranslation } from "react-i18next";
import type { menuItem, menuItems } from "../../../common/components/Icon/ThreeDotsDropdownMenu";
import type { ICustomRemoveButton } from "src/common/components/CustomXIcon/CustomXIcon";
import { useScopeContext } from "./AssistantScenarIaRail/ScopeContext";
import InfoIconWithTooltip from "../../../common/components/Icon/InfoIconWithTooltip";
import { toDisplayText } from "../util/multilineTextFields";

export interface DynamicFormValues {
  [key: string]: unknown;
}

const SECONDS_FIELDS = new Set([
  "time_code",
  "timer",
  "catch_time",
  "game_duration",
  "respond_timer",
  "time_begin",
  "min_silent_time_sec_before_avatar_reaction",
  "max_silent_time_sec_before_avatar_reaction",
]);

export const prettyLabel = (fieldName: string, t: TFunction): string => {
  const key = `pages.project.editNode.fields.${fieldName}`;
  const translated = t(key);
  if (translated && translated !== key) return translated;
  const humanised = fieldName.replace(/_/g, " ");
  return humanised.charAt(0).toUpperCase() + humanised.slice(1);
};

/** Character counter matching the existing MediaNameInput / MediaDetail pattern. */
const CharCounter = ({
  control,
  name,
  maxLength,
  initialValue = "",
}: {
  control: Control<DynamicFormValues>;
  name: string;
  maxLength: number;
  initialValue?: unknown;
}) => {
  const { t } = useTranslation();
  const value: unknown = useWatch({ control, name, defaultValue: initialValue });
  const len = (typeof value === "string" ? value : "").length;
  const isNearLimit = len >= maxLength - 5;
  const isAtLimit = len >= maxLength;
  return (
    <div
      className={`text-sm mt-1 ${
        isAtLimit ? "text-red-600" : isNearLimit ? "text-yellow-600" : "text-gray-500"
      }`}
    >
      {len}/{maxLength} {t("general.characters") || "characters"}
    </div>
  );
};

export const DynamicField = ({
  field,
  register,
  control,
  disabled = false,
  className = undefined,
  staleWarning = false,
  hintText = undefined,
  faithful = false,
  noWrap = false,
}: {
  field: Field;
  register: UseFormRegister<DynamicFormValues>;
  control?: Control<DynamicFormValues>;
  disabled?: boolean;
  className?: string;
  staleWarning?: boolean;
  faithful?: boolean;
  noWrap?: boolean;
  hintText?: string;
}) => {
  const { t } = useTranslation();
  const label = prettyLabel(field.descriptor?.labelKey ?? field.fieldName, t);
  const maxLength = field.descriptor?.jsonSchema?.maxLength;
  const { availableUnits, selectedUnitIds, isRailActive, isPickMode, isUnitSelected, toggleUnit } =
    useScopeContext();
  const scopeUnit = isRailActive
    ? availableUnits.find((u) => u.primaryField === field.fieldName)
    : null;
  const hasSelection = selectedUnitIds.length > 0;
  const fieldInScope = scopeUnit ? isUnitSelected(scopeUnit.id) : false;
  const dim = hasSelection && !fieldInScope;
  const highlight = hasSelection && fieldInScope;
  const wrapperIsScopeTarget = isPickMode && Boolean(scopeUnit);

  const counter =
    maxLength && control ? (
      <CharCounter
        control={control}
        name={field.fieldName}
        maxLength={maxLength}
        initialValue={field.currentValue}
      />
    ) : null;

  const renderFaithfulTextarea = (ctrl: Control<DynamicFormValues>) => (
    <Controller
      control={ctrl}
      name={field.fieldName}
      defaultValue={field.currentValue ?? ""}
      render={({ field: rhf }) => (
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <div className="mt-1">
            <textarea
              value={toDisplayText(rhf.value)}
              onChange={(e) => rhf.onChange(e.target.value)}
              onBlur={rhf.onBlur}
              disabled={disabled}
              maxLength={maxLength}
              rows={4}
              wrap={noWrap ? "off" : undefined}
              className={`${
                className ?? ""
              } appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
            />
          </div>
          {counter}
        </div>
      )}
    />
  );

  const renderField = () => {
    switch (field.type) {
      case "text":
        if (faithful && control) return renderFaithfulTextarea(control);
        return (
          <>
            <Input
              registerFct={() => register(field.fieldName)}
              type="text"
              label={label}
              defaultValue={field.currentValue}
              disabled={disabled}
              className={className}
              maxLength={maxLength}
            />
            {counter}
          </>
        );
      case "longtext":
        if (faithful && control) return renderFaithfulTextarea(control);
        return (
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div className="mt-1">
              <textarea
                {...register(field.fieldName)}
                defaultValue={field.currentValue ?? ""}
                disabled={disabled}
                maxLength={maxLength}
                rows={4}
                className={`${
                  className ?? ""
                } appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
              />
            </div>
            {counter}
          </div>
        );
      case "boolean":
        return (
          <div className="w-full">
            <label
              aria-hidden
              className="invisible block select-none text-sm font-medium text-gray-700"
            >
              {label}
            </label>
            <div className="mt-1">
              <Checkbox
                id={`nodeform-bool-${field.fieldName}`}
                label={label}
                registerFct={() => register(field.fieldName)}
                defaultChecked={!!field.currentValue}
                disabled={disabled}
              />
            </div>
          </div>
        );
      case "arrayOfObjects":
        return (
          <div className="w-full opacity-50">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <div className="mt-1 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-xs text-gray-500 italic">
              {t("pages.project.editNode.arrayFieldSpatialStudioHint")}
            </div>
          </div>
        );
      case "number": {
        const schema = field.descriptor?.jsonSchema ?? {};
        const isPercent = field.descriptor?.displayMode === "percent";
        const isInt = field.descriptor?.displayMode === "integer";
        const rawMin = typeof schema.minimum === "number" ? schema.minimum : undefined;
        const rawMax = typeof schema.maximum === "number" ? schema.maximum : undefined;
        const toDisplay = (raw: unknown): number | "" => {
          if (raw === "" || raw === null || raw === undefined) return "";
          const n = Number(raw);
          if (Number.isNaN(n)) return "";
          return isPercent ? Math.round(n * 100) : n;
        };
        const fromDisplay = (shown: string): number | null => {
          if (shown === "") return null;
          const n = Number(shown);
          if (Number.isNaN(n)) return null;
          return isPercent ? n / 100 : n;
        };
        const displayMin = isPercent && rawMin !== undefined ? rawMin * 100 : rawMin;
        const displayMax = isPercent && rawMax !== undefined ? rawMax * 100 : rawMax;
        const step = isPercent || isInt ? 1 : schema.multipleOf ?? "any";
        return (
          <Controller
            control={control}
            name={field.fieldName}
            defaultValue={field.currentValue ?? ""}
            render={({ field: rhf }) => (
              <div className="w-full">
                <label className="flex items-center gap-1 text-sm font-medium text-gray-700">
                  {label}
                  {field.descriptor?.helpKey ? (
                    <InfoIconWithTooltip message={t(field.descriptor.helpKey)} />
                  ) : SECONDS_FIELDS.has(field.fieldName) ? (
                    <InfoIconWithTooltip message={t("pages.project.editNode.secondsHint")} />
                  ) : null}
                </label>
                <div className="mt-1">
                  <input
                    type="number"
                    value={toDisplay(rhf.value)}
                    min={displayMin}
                    max={displayMax}
                    step={step}
                    disabled={disabled}
                    onChange={(e) => rhf.onChange(fromDisplay(e.target.value))}
                    onBlur={(e) => {
                      rhf.onBlur();
                      if (rawMin !== undefined || rawMax !== undefined) {
                        const raw = fromDisplay(e.target.value);
                        if (raw !== null) {
                          let clamped = raw;
                          if (rawMin !== undefined) clamped = Math.max(rawMin, clamped);
                          if (rawMax !== undefined) clamped = Math.min(rawMax, clamped);
                          if (isInt) clamped = Math.round(clamped);
                          if (Math.abs(clamped - raw) > 1e-10) rhf.onChange(clamped);
                        }
                      }
                    }}
                    className={`${
                      className ?? ""
                    } appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm disabled:cursor-not-allowed disabled:bg-gray-50`}
                  />
                </div>
              </div>
            )}
          />
        );
      }
      case "select": {
        const enumValues: unknown[] = Array.isArray(field.descriptor?.jsonSchema?.enum)
          ? field.descriptor?.jsonSchema?.enum
          : [];
        const options: FieldSelectOption[] =
          field.descriptor?.options ??
          enumValues.map((v) => ({ value: String(v), optionText: String(v) }));
        const currentStr =
          field.currentValue === null || field.currentValue === undefined
            ? ""
            : String(field.currentValue);
        const selectedIdx = options.findIndex((o) => o.value === currentStr);
        return (
          <Dropdown
            label={label}
            width="w-full"
            labelFontClassName="text-sm"
            options={options}
            registerFct={() => register(field.fieldName)}
            disabled={disabled}
            asDefaultValue={selectedIdx >= 0}
            defaultValueIndex={selectedIdx}
          />
        );
      }
      case "color":
        return (
          <Controller
            control={control}
            name={field.fieldName}
            defaultValue={field.currentValue ?? ""}
            render={({ field: rhf }) => (
              <ColorField
                label={label}
                value={rhf.value as string}
                onChange={rhf.onChange}
                disabled={disabled}
                className={className}
              />
            )}
          />
        );
      default:
        break;
    }
    return <></>;
  };

  const rendered = (
    <>
      {renderField()}
      {hintText ? <p className="mt-1 text-xs italic text-gray-500">{hintText}</p> : null}
    </>
  );

  const wrapperBaseClass = "group relative w-full";
  const wrapperTransitionClass = dim ? "opacity-50 transition-opacity" : "transition-opacity";
  const ringClass = highlight ? "ring-2 ring-indigo-300 rounded-md p-1 -m-1" : "";
  const pickModeClass = wrapperIsScopeTarget
    ? "cursor-pointer ring-1 ring-dashed ring-indigo-200 rounded-md"
    : "";

  const scopeWrapperProps = wrapperIsScopeTarget
    ? {
        "data-scope-wrapper": "true" as const,
        role: "button" as const,
        tabIndex: -1,
        onMouseDownCapture: (e: ReactMouseEvent) => {
          e.preventDefault();
        },
        onClick: (e: ReactMouseEvent) => {
          if (!scopeUnit) return;
          e.stopPropagation();
          toggleUnit(scopeUnit.id);
        },
      }
    : {};

  const wrapperClassName =
    `${wrapperBaseClass} ${wrapperTransitionClass} ${ringClass} ${pickModeClass}`
      .replace(/\s+/g, " ")
      .trim();

  if (!staleWarning) {
    return (
      <div className={wrapperClassName} {...scopeWrapperProps}>
        {rendered}
      </div>
    );
  }

  return (
    <div className={wrapperClassName} {...scopeWrapperProps}>
      <div className="mb-1">
        <span className="badge-warning text-xs">
          {t("pages.project.editNode.staleTranslation")}
        </span>
      </div>
      {rendered}
    </div>
  );
};

export const DynamicMedia = ({
  fieldType,
  handleButtonClick,
  media,
  currentMedia,
  field,
  customMenuItems,
  disabled = false,
  CustomRemoveButton = undefined,
  className = undefined,
  showThreeDotsMenu = true,
  isLoading = false,
}: {
  field: ControllerRenderProps;
  fieldType: string;
  register: unknown;
  handleButtonClick: (mediaId: number, item: menuItem | null, index: number) => void;
  media: IMedia;
  currentMedia: number | undefined;
  customMenuItems?: menuItems;
  disabled?: boolean;
  stopPlayingMedia?: boolean;
  CustomRemoveButton?: ICustomRemoveButton;
  className?: string;
  showThreeDotsMenu?: boolean;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const med = media?.id
    ? media
    : ({
        createdAt: "",
        id: -1,
        name: `${t("pages.gallery.noMedia")}`,
        label: "",
        organization: emptyOrganization(3),
        s3_url: "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/NOMEDIA.png",
        type: "image",
        updatedAt: "",
        filename: "NOMEDIA.png",
        tags: [],
        upload_status: "completed",
      } as IMedia);
  const currentMed = Number(currentMedia) > 0 ? currentMedia : -1;
  switch (fieldType) {
    case "media":
      return (
        <MediaSelector
          field={field}
          handleButtonClick={handleButtonClick}
          media={med}
          currentMedia={currentMed}
          customMenuItems={customMenuItems}
          disabled={disabled}
          isLoading={isLoading}
          CustomRemoveButton={CustomRemoveButton}
          className={className}
          showThreeDotsMenu={showThreeDotsMenu}
        />
      );
  }
  return <></>;
};
